import React, { memo, useContext, useEffect, useMemo, useState } from 'react'
import { useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Box } from '@node-space/ui-components'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { FiatSendStep, FiatSendSteps } from 'types/walletActions'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { selectedFiatBeneficiaryAtom } from '../Pay/atoms/atoms'
import BeneficiaryForm from './components/BeneficiaryForm'
import { Complete } from './components/Complete'
import { Confirmation } from './components/Confirmation'
import ContactBankAccountForm from './components/ContactBankAccountForm'
import {
  ContactOrBeneficiary,
  ContactOrBeneficiarySelector,
} from './components/ContactOrBeneficiarySelector'
import { PaymentDetails } from './components/PaymentDetails'
import { useFiatSendForm } from './hooks/useFiatSendForm'

const FiatSend = () => {
  const { t } = useTranslation()
  const { enableContactBankAccounts } = useFeatureFlags()

  const [walletAction, setWalletAction] = useContext(WalletActionContext)

  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)

  const [step, setStep] = useState<FiatSendStep>(
    enableContactBankAccounts
      ? FiatSendSteps.CONTACT_OR_BENEFICIARY_SELECTOR
      : FiatSendSteps.BENEFICIARY
  )

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_INITIATE, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.VIEW,
    })
  }, [])

  useFiatSendForm()

  const sheetHeader = useMemo(() => {
    switch (step) {
      case FiatSendSteps.CONTACT_OR_BENEFICIARY_SELECTOR:
      case FiatSendSteps.CONTACT_BANK_ACCOUNT:
      case FiatSendSteps.BENEFICIARY:
        return t('wallets.fiatSend.beneficiaryStepHeader')
      case FiatSendSteps.DETAILS:
        return t('wallets.fiatSend.amountStepHeader')
      case FiatSendSteps.CONFIRMATION:
        return t('wallets.fiatSend.confirmationHeader')
      case FiatSendSteps.EOF:
        return null
      default:
        return ''
    }
  }, [step])

  const canGoBack = useMemo(() => {

    if (enableContactBankAccounts) {
      switch (step) {
        case FiatSendSteps.BENEFICIARY:
        case FiatSendSteps.CONTACT_BANK_ACCOUNT:
        case FiatSendSteps.DETAILS:
        case FiatSendSteps.CONFIRMATION:
          return true
        case FiatSendSteps.CONTACT_OR_BENEFICIARY_SELECTOR:
        case FiatSendSteps.EOF:
        default:
          return false
      }
    }



    switch (step) {
      case FiatSendSteps.DETAILS:
      case FiatSendSteps.CONFIRMATION:
        return true
      case FiatSendSteps.BENEFICIARY:
      case FiatSendSteps.CONTACT_BANK_ACCOUNT:
      case FiatSendSteps.EOF:
      default:
        return false
    }
  }, [step])

  const handleOnBack = () => {
    if (canGoBack) {
      switch (step) {
        case FiatSendSteps.DETAILS:
          setStep(FiatSendSteps.BENEFICIARY)
          setBeneficiaryAtom(null)
          return
        case FiatSendSteps.CONFIRMATION:
          setStep(FiatSendSteps.DETAILS)
          return
        case FiatSendSteps.BENEFICIARY:
        case FiatSendSteps.CONTACT_BANK_ACCOUNT:
          setStep(FiatSendSteps.CONTACT_OR_BENEFICIARY_SELECTOR)
          return
        case FiatSendSteps.EOF:
        default:
      }
    }
  }

  const handleOnContactOrBeneficiaryClick = (value: string) => {
    if (value === ContactOrBeneficiary.CONTACT) {
      setStep(FiatSendSteps.CONTACT_BANK_ACCOUNT)
    } else {
      setStep(FiatSendSteps.BENEFICIARY)
    }
  }

  useEffect(() => {
    setWalletAction({
      ...walletAction,
      header: sheetHeader,
      onBack: canGoBack ? handleOnBack : undefined,
      onClose: () => {
        track.Amp.track(AmplitudeEvent.FIAT_PAYOUT_CLOSE, {
          category: AmplitudeEventCategory.MERCHANT_PORTAL,
          action: AmplitudeEventAction.CLICK,
          step,
        })

        walletAction?.onClose()
      },
    })
  }, [step, canGoBack])

  return (
    <Box width="full">
      {step === FiatSendSteps.CONTACT_OR_BENEFICIARY_SELECTOR && (
        <ContactOrBeneficiarySelector
          onContactOrBeneficiaryClick={handleOnContactOrBeneficiaryClick}
        />
      )}
      {step === FiatSendSteps.CONTACT_BANK_ACCOUNT && <ContactBankAccountForm setStep={setStep} />}
      {step === FiatSendSteps.BENEFICIARY && <BeneficiaryForm setStep={setStep} />}
      {step === FiatSendSteps.DETAILS && <PaymentDetails setStep={setStep} />}
      {step === FiatSendSteps.CONFIRMATION && <Confirmation setStep={setStep} />}
      {step === FiatSendSteps.EOF && <Complete />}
    </Box>
  )
}

export default memo(FiatSend)
